import {PushBootstrap} from './PushNotifications';
import {detachPush} from './push-client';
import { WorkspaceNavigation, WorkspaceFinder } from "./WorkspaceNavigation";
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Layers,
  Users,
  Wallet,
  Truck,
  RotateCcw,
  Settings,
  Menu,
  X,
  LogOut,
  ArrowUpRight,
  Store,
  BarChart3,
  Globe,
  Image,
  ShieldCheck,
  Bell,
  LifeBuoy,
  Tag,
  MapPin,
  History,
} from "lucide-react";
import { account } from "../appwrite";
import { useStateValue } from "../StateProvider";
import { api, clearApiSession } from "./api";
import { Button, ErrorState, Skeleton } from "./ui";
import logo from "../Components/Assets/images/logo.png";
import "./commerce.css";
import "./operations.css";
const HubWorkspace=lazy(()=>import('./HubWorkspace'));
const HubSettings=lazy(()=>import('./HubWorkspace').then(m=>({default:m.HubSettings})));
const StaffNotifications=lazy(()=>import('./PushNotifications').then(m=>({default:m.StaffNotifications})));
const PushCampaigns=lazy(()=>import('./PushCampaigns'));
const AdminLiveChat = lazy(() => import("./AdminLiveChat"));
const AccountSecurity = lazy(() => import("./AccountSecurity"));
const Auth = lazy(() => import("../auth/Auth"));
const ProductEditor = lazy(() => import("./ProductEditor"));
const named = (name) =>
  lazy(() => import("./OperationsPages").then((m) => ({ default: m[name] })));
const Overview = named("Overview"),
  Resource = named("Resource"),
  OrderWorkspace = named("OrderWorkspace"),
  Inventory = named("Inventory"),
  Suppliers = named("Suppliers"),
  Finance = named("Finance"),
  Merchandising = named("Merchandising"),
  CommerceSettings = named("CommerceSettings"),
  SellerSetup = named("SellerSetup"),
  Analytics = named("Analytics");
const adminGroups = [
  [
    "Workspace",
    [
      ["Overview", "", LayoutDashboard],
      ["Notifications", "notifications", Bell],
    ],
  ],
  [
    "Catalogue",
    [
      ["Products", "all-products", Package],
      ["Categories", "categories", Layers],
      ["Collections", "collections", Tag],
      ["Brands", "brands", Store],
      ["Media", "media", Image],
    ],
  ],
  [
    "Merchandising",
    [
      ["Homepage", "homepage", Globe],
      ["Promotions", "promotions", Tag],
      ["Push campaigns", "campaigns", Bell],
    ],
  ],
  [
    "Orders",
    [
      ["Orders", "orders", ShoppingBag],
      ["Hub", "hub", Package],
      ["Fulfilments", "fulfilments", Truck],
      ["Shipments", "shipments", MapPin],
      ["Returns", "returns", RotateCcw],
    ],
  ],
  [
    "Supply",
    [
      ["Connections & imports", "suppliers", Globe],
      ["Inventory", "inventory", Package],
      ["Locations", "locations", MapPin],
      ["Sync history", "sync-history", History],
    ],
  ],
  [
    "People",
    [
      ["Sellers & verification", "sellers", Store],
      ["Customers", "customers", Users],
      ["Support", "support", LifeBuoy],
      ["Live chat", "live-chat", LifeBuoy],
      ["Reviews", "reviews", Tag],
    ],
  ],
  [
    "Finance",
    [
      ["Payments", "payments", Wallet],
      ["Refunds", "refunds", RotateCcw],
      ["Payout review", "payouts", Wallet],
      ["Ledger", "ledger", Layers],
    ],
  ],
  [
    "Management",
    [
      ["Analytics", "analytics", BarChart3],
      ["Staff & permissions", "staff", ShieldCheck],
      ["Settings", "settings", Settings],
      ["Account security", "security", ShieldCheck],
      ["Audit history", "audit", History],
    ],
  ],
];
const sellerGroups = [
  [
    "Your workspace",
    [
      ["Overview", "", LayoutDashboard],
      ["Products", "my-vault", Package],
      ["Orders", "orders", ShoppingBag],
      ["Inventory", "inventory", Layers],
      ["Returns", "returns", RotateCcw],
      ["Earnings & payouts", "earnings", Wallet],
      ["Analytics", "analytics", BarChart3],
    ],
  ],
  [
    "Your business",
    [
      ["Store & onboarding", "store", Store],
      ["Notifications", "notifications", Bell],
      ["Support", "support", LifeBuoy],
      ["Settings", "settings", Settings],
      ["Account security", "security", ShieldCheck],
    ],
  ],
];
function Content({ mode }) {
  const [{ user }, dispatch] = useStateValue();
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [nav, setNav] = useState(false);
  const location = useLocation();
  const prefix = mode === "admin" ? "/d" : "/s";
  useEffect(() => {
    let live = true;
    setReady(false);
    setError(null);
    setIdentity(null);
    account
      .get()
      .then(async (user) => {
        if (live) dispatch({ type: "SET_USER", user });
        const me = await api("/me");
        if (live) setIdentity(me);
      })
      .catch((e) => {
        if (live && e.status !== 401 && e.code !== 401) setError(e);
      })
      .finally(() => {
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [dispatch, user?.$id, user?.emailVerification]);
  useEffect(() => {
    setNav(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  useEffect(() => {
    if (!nav) return;
    const previous = document.activeElement;
    const rail = document.querySelector(".tb-ops-rail");
    const focusable = () =>
      Array.from(rail.querySelectorAll("a,button")).filter(
        (el) => el.getClientRects().length,
      );
    focusable()[1]?.focus();
    const key = (event) => {
      if (event.key === "Escape") setNav(false);
      if (event.key === "Tab") {
        const nodes = focusable(),
          first = nodes[0],
          last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      previous?.focus();
    };
  }, [nav]);
  const authRoutes = {
    "/login": "login",
    "/signup": "signup",
    "/auth/verify/otp": "otp",
    "/reset-password": "recovery",
    "/account/reset-password": "recovery",
    "/account/change-password": "password",
    "/account/verify-email": "verify",
    "/account/email-verification": "verified",
  };
  if (
    mode === "admin" &&
    ["/signup", "/auth/verify/otp"].includes(location.pathname)
  )
    return <Navigate replace to={`/login?next=${encodeURIComponent(location.pathname+location.search)}`} />;
  if (authRoutes[location.pathname])
    return (
      <Auth
        key={location.pathname}
        mode={mode}
        flow={authRoutes[location.pathname]}
      />
    );
  if (!ready)
    return (
      <div className="tb-ops tb-ops-loading">
        <Skeleton count={3} />
      </div>
    );
  if (error)
    return (
      <div className="tb-ops tb-ops-loading">
        <ErrorState error={error} retry={() => window.location.reload()} />
        <Link className="tb-text-link" to="/login">
          Return to sign in
        </Link>
      </div>
    );
  if (!identity)
    return (
      <Navigate
        replace
        to={`/login?next=${encodeURIComponent(location.pathname+location.search)}`}
      />
    );
  if (mode === "admin" && !identity.admin && !identity.staffRole)
    return (
      <div className="tb-ops tb-ops-loading">
        <ErrorState error="This account does not have access to TrendBay operations." />
        <Button
          onClick={async () => {
            if(mode==='admin')try{await detachPush('admin');}catch{try{const r=await navigator.serviceWorker?.getRegistration('/');await (await r?.pushManager.getSubscription())?.unsubscribe();}catch{}}
              await account.deleteSession("current");
            clearApiSession();
            window.location.assign("/login");
          }}
        >
          Sign out
        </Button>
      </div>
    );
  if (
    mode === "seller" &&
    !identity.seller &&
    !["/onboarding", "/s/store"].includes(location.pathname)
  )
    return <Navigate replace to="/onboarding" />;
  // Staff roles see only the sections their role covers; the server enforces the same rules.
  const allowedSections = mode === "admin" && !identity.admin ? identity.staffSections || [] : null;
  const groups = mode === "admin"
    ? adminGroups
        .map(([label, links, ...rest]) => [label, links.filter(([, path]) => !allowedSections || allowedSections.includes(path === "all-products" ? "products" : path)), ...rest])
        .filter(([, links]) => links.length)
    : sellerGroups;
  const current =
    groups
      .flatMap((g) => g[1])
      .find(
        ([, path]) =>
          location.pathname === `${prefix}${path ? `/${path}` : ""}`,
      )?.[0] || "Product workspace";
  return (
    <div className={`tb-ops tb-ops--${mode}`}>
      {mode==='admin'&&<PushBootstrap userId={user?.$id} portal="admin"/>}
      <a className="tb-skip" href="#workspace">
        Skip to workspace
      </a>
      {nav && (
        <button
          className="tb-ops-backdrop"
          aria-label="Close navigation"
          onClick={() => setNav(false)}
        />
      )}
      <aside className={`tb-ops-rail ${nav ? "is-open" : ""}`}>
        <Link className="tb-ops-brand" to={prefix}>
          <img src={logo} alt="TrendBay" />
          <span>{mode === "admin" ? "OPERATIONS" : "SELLER STUDIO"}</span>
        </Link>
        <button
          className="tb-ops-rail-close"
          aria-label="Close navigation"
          onClick={() => setNav(false)}
        >
          <X size={20} />
        </button>
        <WorkspaceNavigation groups={groups} prefix={prefix} mode={mode} />
        <div className="tb-ops-person">
          <span>{identity.name?.charAt(0) || "T"}</span>
          <div>
            <strong>{identity.name || "Your account"}</strong>
            <small>
              {mode === "admin"
                ? "Administrator"
                : identity.seller?.title || "New seller"}
            </small>
          </div>
          <button
            aria-label="Sign out"
            onClick={async () => {
              if(mode==='admin')try{await detachPush('admin');}catch{try{const r=await navigator.serviceWorker?.getRegistration('/');await (await r?.pushManager.getSubscription())?.unsubscribe();}catch{}}
              await account.deleteSession("current");
              clearApiSession();
              window.location.assign("/login");
            }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <div className="tb-ops-stage">
        <header className="tb-ops-topbar">
          <button
            className="tb-ops-menu"
            aria-label="Open navigation"
            onClick={() => setNav(true)}
          >
            <Menu size={20} />
          </button>
          <span>
            {mode === "admin" ? "Operations" : "Seller studio"}
            <i>/</i>
            <strong>{current}</strong>
          </span>
          <WorkspaceFinder groups={groups} prefix={prefix} />
          <a
            href="https://mart.trendbay.ng"
            target="_blank"
            rel="noreferrer"
          >
            Visit Bay Area
            <ArrowUpRight size={14} />
          </a>
          <Link to={`${prefix}/notifications`} aria-label="Notifications">
            <Bell size={18} />
          </Link>
        </header>
        <main id="workspace" className="tb-ops-main">
          {mode === "seller" && !identity.verified && (
            <div className="tb-ops-verification">
              Verify your email before submitting your store.
              <Link to="/account/verify-email">
                Verify email
                <ArrowUpRight size={13} />
              </Link>
            </div>
          )}
          <Routes>
            {mode==='admin'&&<><Route path={`${prefix}/hub`} element={<HubWorkspace identity={identity}/>}/><Route path={`${prefix}/hub-settings`} element={identity.admin?<HubSettings/>:<Navigate replace to={`${prefix}/hub`}/>}/><Route path={`${prefix}/campaigns`} element={identity.admin?<PushCampaigns/>:<Navigate replace to={prefix}/>}/><Route path={`${prefix}/notifications`} element={<StaffNotifications/>}/></>}
            <Route
              path={`${prefix}/security`}
              element={
                <div className="tb-section">
                  <AccountSecurity />
                </div>
              }
            />
            <Route path="/" element={<Navigate replace to={prefix} />} />
            <Route
              path="/onboarding"
              element={
                <SellerSetup
                  identity={identity}
                  onSaved={(seller) => setIdentity((i) => ({ ...i, seller }))}
                />
              }
            />
            <Route
              path={prefix}
              element={<Overview mode={mode} identity={identity} />}
            />
            <Route
              path={`${prefix}/add-product`}
              element={<ProductEditor mode={mode} />}
            />
            <Route
              path={`${prefix}/edit-product/:id`}
              element={<ProductEditor mode={mode} />}
            />
            <Route
              path={`${prefix}/order/:id`}
              element={<OrderWorkspace mode={mode} />}
            />
            <Route
              path={`${prefix}/inventory`}
              element={<Inventory mode={mode} />}
            />
            {mode === "admin" && (
              <Route path={`${prefix}/live-chat`} element={<AdminLiveChat />} />
            )}
            <Route path={`${prefix}/suppliers`} element={<Suppliers />} />
            <Route path={`${prefix}/homepage`} element={<Merchandising />} />
            <Route
              path={`${prefix}/settings`}
              element={
                mode === "admin" ? (
                  <CommerceSettings />
                ) : (
                  <SellerSetup
                    identity={identity}
                    onSaved={(seller) => setIdentity((i) => ({ ...i, seller }))}
                  />
                )
              }
            />
            <Route
              path={`${prefix}/store`}
              element={
                <SellerSetup
                  identity={identity}
                  onSaved={(seller) => setIdentity((i) => ({ ...i, seller }))}
                />
              }
            />
            <Route path={`${prefix}/earnings`} element={<Finance />} />
            <Route
              path={`${prefix}/analytics`}
              element={<Analytics mode={mode} />}
            />
            <Route
              path={`${prefix}/:section`}
              element={<Resource mode={mode} />}
            />
            <Route path="*" element={<Navigate replace to={prefix} />} />
          </Routes>
        </main>
        <footer className="tb-ops-footer">
          <span>
            TrendBay {mode === "admin" ? "Operations" : "Seller Studio"}
          </span>
          <span>Made for a well-run Bay Area.</span>
        </footer>
      </div>
    </div>
  );
}
export default function OperationsApp({ mode }) {
  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="tb-ops tb-ops-loading">
            <Skeleton count={3} />
          </div>
        }
      >
        <Content mode={mode} />
      </Suspense>
    </BrowserRouter>
  );
}
